<template>
  <MultiSelect
    v-model="selectedEntities"
    :options="entityOptions"
    option-label="label"
    track-by="value"
    :placeholder="placeholder"
    :disabled="loading || disabled"
    :show-tags="showTags"
    :max-count="maxCount"
    :class="containerClass"
    :style="{ width: width, maxWidth: maxWidth }"
    :enable-refresh="enableRefresh"
    :refresh-loading="refreshLoading"
    :modal-popover="true"
    :show-select-all="false"
    :as-child="enableFilterIcon"
    @refresh="$emit('refresh')">
    
    <template v-if="enableFilterIcon">
      <Button
        variant="outline"
        :disabled="loading || disabled"
        :style="{ width: width, maxWidth: maxWidth }"
        class="h-9 inline-flex w-full justify-center items-center px-3 text-sm font-medium min-w-0">
        <Filter
          :class="['h-4 w-4 mr-1', hasSelection ? 'text-primary' : '']"
          :fill="hasSelection ? 'currentColor' : 'none'" />
        <span class="px-1">
          {{ hasSelection ? `${selectedEntities.length} selected` : placeholder }}
        </span>
        <ChevronDown class="-mr-1 ml-2 h-5 w-5" />
      </Button>
    </template>
    
    <template v-if="showIcons" #option="{ option }">
      <div class="flex gap-2 items-center">
        <component
          :is="entityIcon"
          v-if="entityIcon"
          class="h-4 w-4 flex-shrink-0"
        />
        <span>{{ option.label }}</span>
      </div>
    </template>
  </MultiSelect>
</template>

<script setup>
import { computed } from 'vue'
import { MultiSelect } from '@/components/ui/multi-select'
import { Button } from '@/components/ui/button'
import { CloudDownload, CloudUpload, ArrowUpDown, Server, Database, Filter, ChevronDown } from 'lucide-vue-next'

// Entity type to icon mapping - defined before defineProps
const ENTITY_ICONS = {
    sources: CloudDownload,
    destinations: CloudUpload,
    pipelines: ArrowUpDown,
    connectors: Server,
    generic: Database
}

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => []
    },
    entities: {
        type: Array,
        required: true
    },
    placeholder: {
        type: String,
        default: 'Select entities...'
    },
    loading: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    showTags: {
        type: Boolean,
        default: true
    },
    maxCount: {
        type: Number,
        default: 3
    },
    width: {
        type: String,
        default: 'auto'
    },
    maxWidth: {
        type: String,
        default: '14rem'
    },
    containerClass: {
        type: String,
        default: ''
    },
    enableRefresh: {
        type: Boolean,
        default: false
    },
    refreshLoading: {
        type: Boolean,
        default: false
    },
    showIcons: {
        type: Boolean,
        default: false
    },
    entityType: {
        type: String,
        default: 'generic',
        validator: (value) => ['sources', 'destinations', 'pipelines', 'connectors', 'generic'].includes(value)
    },
    enableFilterIcon: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:modelValue', 'refresh'])

const selectedEntities = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
})

const hasSelection = computed(() => {
    return props.modelValue && props.modelValue.length > 0
})

const entityOptions = computed(() => {
    if (!props.entities?.length) return []
  
    return props.entities.map(entity => {
    // Handle string entities
        if (typeof entity === 'string') {
            return { value: entity, label: entity }
        }
    
        // Handle object entities with standard properties
        const value = entity.id || entity.value || entity.name
        const label = entity.name || entity.label || entity.displayValue || value
    
        return { value, label }
    })
})

const entityIcon = computed(() => {
    return props.showIcons ? ENTITY_ICONS[props.entityType] : null
})
</script>